import * as d3 from 'd3';
import React from 'react';

import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import {SWEAnalysisResponse, fetchSWEAnalysis} from 'src/lib/api/analysis';
import COLORS from 'src/lib/colors';
import {Dims} from 'src/lib/commonTypes';
import {annualLinesCsvRows, boxplotCsvRows, elevationProfileCsvRows} from 'src/lib/sweCsv';
import {getGraphDims} from 'src/lib/vizUtils';

import {recordEvent} from './Analytics';
import Axis, {defaultNumericTickFormat, defaultTooltipFormat} from './D3Axis';
import Legend, {LegendConfig, makeLine, makeRect} from './Legend';
import {useTranslations} from './TranslationsContext';
import {MARGIN} from './Viz';
import ChartFrame from './Viz/ChartFrame';
import WithWidth from './WithWidth';

const CHART_HEIGHT = 300;
const PROFILE_HEIGHT = 440;
const BAND_MARGIN = {...MARGIN, bottom: 48};

const LOW_YEAR_COLORS = ['#2471a3', '#8e44ad', '#2e86c1'];
const HIGH_YEAR_COLORS = ['#1e8449', '#d4ac0d', '#117a65'];
const PREV_YEAR_COLORS = ['#e67e22', '#e74c3c'];
const CURRENT_COLOR = '#dc143c';
const CLIMATOLOGY_COLOR = '#32cd32';

// ── Shared helpers ──────────────────────────────────────────────────────────

function SparkleIcon() {
  return (
    <svg viewBox="0 0 20 20" className="w-4 h-4 mr-1">
      <g>
        <polygon fill="currentColor" points="7 0 5 5 0 7 5 9 7 14 9 9 14 7 9 5" />
        <polygon
          fill="currentColor"
          points="15 10 13.5 13.5 10 15 13.5 16.5 15 20 16.5 16.5 20 15 16.5 13.5"
        />
      </g>
    </svg>
  );
}

function StaticLegend({configs}: {configs: LegendConfig[]}) {
  const visibleById = React.useMemo(
    () => Object.fromEntries(configs.map((c) => [c.id, true])),
    [configs]
  );
  const noop = React.useCallback(() => {}, []);
  return (
    <div style={{pointerEvents: 'none'}}>
      <Legend configs={configs} isVisibleById={visibleById} onChange={noop} />
    </div>
  );
}

const TOOLTIP_WIDTH = 240;

/** Readout anchored beside the hovered position, flipping sides at the midpoint. */
function HoverTooltip({x, width, children}: {x: number; width: number; children: React.ReactNode}) {
  // Keep the box inside the chart even where there isn't room beside the cursor.
  const maxInset = Math.max(0, width - TOOLTIP_WIDTH);
  const offset =
    x < width / 2 ? {left: Math.min(x + 8, maxInset)} : {right: Math.min(width - x + 8, maxInset)};
  return (
    <div
      className="absolute top-0 z-10 rounded shadow-sm border border-gray-300 bg-white text-xs p-1.5 pointer-events-none"
      style={{maxWidth: Math.min(TOOLTIP_WIDTH, width), ...offset}}
    >
      {children}
    </div>
  );
}

function TooltipRow({label, value}: {label: string; value: string}) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-600">{label}</span>
      <span className="font-mono whitespace-nowrap">{value}</span>
    </div>
  );
}

// ── Elevation Band Boxplot ──────────────────────────────────────────────────

function makeDiamond({fill, stroke}: {fill: string; stroke: string}): React.ReactElement {
  const s = 16;
  const c = s / 2;
  const r = s * 0.35;
  return (
    <polygon
      points={`${c},${c - r} ${c + r},${c} ${c},${c + r} ${c - r},${c}`}
      fill={fill}
      stroke={stroke}
      strokeWidth={1}
    />
  );
}

function ElevationBandBoxplot({
  data,
  width,
  volumeUnit,
  elevationUnit,
  siteName,
}: {
  data: SWEAnalysisResponse['elevationBandBoxplot'];
  width: number;
  volumeUnit: string;
  elevationUnit: string;
  siteName: string;
}) {
  const {t} = useTranslations();
  const {bands, stats, currentValues} = data;
  const [hoveredBand, setHoveredBand] = React.useState<number | null>(null);

  const dims: Dims = [width, CHART_HEIGHT];
  const graphDims = getGraphDims(dims, BAND_MARGIN);

  const allVals = stats.flatMap((s) => [s.min, s.max]);
  currentValues.forEach((v) => {
    if (v != null) allVals.push(v);
  });
  const yMax = allVals.length > 0 ? Math.max(...allVals) * 1.05 : 1;

  const x = d3
    .scaleBand()
    .domain(bands)
    .range([0, graphDims[0]])
    .paddingInner(0.3)
    .paddingOuter(0.15);
  const y = d3.scaleLinear([0, yMax], [graphDims[1], 0]);
  const boxW = x.bandwidth() * 0.7;

  const formatVolume = (v: number) => `${defaultTooltipFormat(v)} ${volumeUnit}`;
  const bandCenter = (i: number) => (x(bands[i]) ?? 0) + x.bandwidth() / 2;
  /** Full-height column covering one band and the gutter around it. */
  const bandColumn = (i: number) => ({
    x: (x(bands[i]) ?? 0) - (x.step() - x.bandwidth()) / 2,
    y: 0,
    width: x.step(),
    height: graphDims[1],
  });

  return (
    <ChartFrame
      title={t('sweVolumeByElevationBandYears', {
        numYears: String(data.numYears),
        yearStart: String(data.yearRange[0]),
        yearEnd: String(data.yearRange[1]),
      })}
      siteName={siteName}
      getCsvRows={() => boxplotCsvRows(data, volumeUnit, elevationUnit)}
    >
      <div className="relative">
        <svg width={dims[0]} height={dims[1]}>
          <g transform={`translate(${BAND_MARGIN.left}, ${BAND_MARGIN.top})`}>
            <Axis
              axisCreator={d3.axisLeft(y).tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
              yLabelProps={{text: t('sweVolumeWithUnit', {unit: volumeUnit}), graphDims}}
            />
            <g transform={`translate(0, ${graphDims[1]})`}>
              <Axis axisCreator={d3.axisBottom(x).tickSizeOuter(0)} />
              <text
                x={graphDims[0] / 2}
                dy={36}
                textAnchor="middle"
                fontSize={12}
                className="fill-current text-gray-500"
              >
                {t('elevationBandWithUnit', {unit: elevationUnit})}
              </text>
            </g>

            {hoveredBand != null && (
              <rect {...bandColumn(hoveredBand)} fill={COLORS.AXIS_LIGHT} opacity={0.25} />
            )}

            {stats.map((s, i) => {
              const cx = bandCenter(i);
              return (
                <g key={i}>
                  <line x1={cx} x2={cx} y1={y(s.max)} y2={y(s.min)} stroke={COLORS.AXIS} />
                  <line
                    x1={cx - boxW / 4}
                    x2={cx + boxW / 4}
                    y1={y(s.max)}
                    y2={y(s.max)}
                    stroke={COLORS.AXIS}
                  />
                  <line
                    x1={cx - boxW / 4}
                    x2={cx + boxW / 4}
                    y1={y(s.min)}
                    y2={y(s.min)}
                    stroke={COLORS.AXIS}
                  />
                  <rect
                    x={cx - boxW / 2}
                    y={y(s.q75)}
                    width={boxW}
                    height={y(s.q25) - y(s.q75)}
                    fill={COLORS.AXIS_LIGHT}
                    stroke={COLORS.AXIS}
                    strokeWidth={1}
                  />
                  <line
                    x1={cx - boxW / 2}
                    x2={cx + boxW / 2}
                    y1={y(s.median)}
                    y2={y(s.median)}
                    stroke={COLORS.AXIS}
                    strokeWidth={2}
                  />
                </g>
              );
            })}

            {currentValues.map((v, i) => {
              if (v == null) return null;
              const cx = bandCenter(i);
              const cy = y(v);
              const s = 5;
              return (
                <polygon
                  key={i}
                  points={`${cx},${cy - s} ${cx + s},${cy} ${cx},${cy + s} ${cx - s},${cy}`}
                  fill={CURRENT_COLOR}
                  stroke="#8b0000"
                  strokeWidth={1}
                />
              );
            })}

            {bands.map((band, i) => (
              <rect
                key={band}
                {...bandColumn(i)}
                fill="transparent"
                onMouseEnter={() => setHoveredBand(i)}
                onMouseLeave={() => setHoveredBand(null)}
              />
            ))}
          </g>
        </svg>

        {hoveredBand != null && (
          <HoverTooltip x={BAND_MARGIN.left + bandCenter(hoveredBand)} width={dims[0]}>
            <div className="font-semibold text-center pb-0.5">{bands[hoveredBand]}</div>
            <TooltipRow
              label={t('currentYear')}
              value={
                currentValues[hoveredBand] != null ? formatVolume(currentValues[hoveredBand]!) : '—'
              }
            />
            <TooltipRow label={t('median')} value={formatVolume(stats[hoveredBand].median)} />
          </HoverTooltip>
        )}
      </div>
      <StaticLegend
        configs={[
          {
            id: 'iqr',
            display: t('interquartileRange'),
            makeIcon: () => makeRect({fill: COLORS.AXIS_LIGHT, stroke: COLORS.AXIS}),
          },
          {
            id: 'current',
            display: t('currentYear'),
            makeIcon: () => makeDiamond({fill: CURRENT_COLOR, stroke: '#8b0000'}),
          },
        ]}
      />
    </ChartFrame>
  );
}

// ── Annual Lines ────────────────────────────────────────────────────────────

function HoverableLegend({
  configs,
  hoveredId,
  onHover,
}: {
  configs: LegendConfig[];
  hoveredId: string | null;
  onHover: (id: string | null) => void;
}) {
  return (
    <div className="text-xs bg-white pt-2 pb-4">
      <div className="flex flex-row flex-wrap gap-2 justify-center">
        {configs.map(({id, display, makeIcon}) => (
          <div
            key={id}
            className={`flex items-center mx-2 cursor-default ${
              hoveredId != null && hoveredId !== id ? 'opacity-40' : ''
            }`}
            onMouseEnter={() => onHover(id)}
            onMouseLeave={() => onHover(null)}
          >
            <svg width={16} height={16} className="mr-1">
              {makeIcon(0, 1)}
            </svg>
            <span>{display}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnualLines({
  data,
  width,
  volumeUnit,
  elevationUnit,
  siteName,
}: {
  data: SWEAnalysisResponse['annualLines'];
  width: number;
  volumeUnit: string;
  elevationUnit: string;
  siteName: string;
}) {
  const {t} = useTranslations();
  const {bands, years, current, highlighted} = data;
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const dims: Dims = [width, CHART_HEIGHT];
  const graphDims = getGraphDims(dims, BAND_MARGIN);

  const allVals = [...years.flatMap((yr) => yr.values), ...current.values].filter(
    (v): v is number => v != null
  );
  const yMax = allVals.length > 0 ? Math.max(...allVals) * 1.05 : 1;

  const x = d3
    .scaleBand()
    .domain(bands)
    .range([0, graphDims[0]])
    .paddingInner(0.3)
    .paddingOuter(0.15);
  const y = d3.scaleLinear([0, yMax], [graphDims[1], 0]);

  const makePath = (values: (number | null)[]) =>
    values
      .map((v, i) =>
        v != null ? `${i === 0 ? 'M' : 'L'}${(x(bands[i]) ?? 0) + x.bandwidth() / 2},${y(v)}` : ''
      )
      .filter(Boolean)
      .join(' ');

  const highlightSet = new Set([
    ...highlighted.lowYears,
    ...highlighted.highYears,
    ...highlighted.previousYears,
  ]);

  const getYearStyle = (
    year: number
  ): {id: string; stroke: string; strokeWidth: number; dasharray: string} | null => {
    const li = highlighted.lowYears.indexOf(year);
    if (li >= 0)
      return {id: `low-${year}`, stroke: LOW_YEAR_COLORS[li], strokeWidth: 1.4, dasharray: '4 2'};
    const hi = highlighted.highYears.indexOf(year);
    if (hi >= 0)
      return {id: `high-${year}`, stroke: HIGH_YEAR_COLORS[hi], strokeWidth: 1.4, dasharray: '4 2'};
    const pi = highlighted.previousYears.indexOf(year);
    if (pi >= 0)
      return {id: `prev-${year}`, stroke: PREV_YEAR_COLORS[pi], strokeWidth: 1.8, dasharray: ''};
    return null;
  };

  const hoverWidth = (baseWidth: number, id: string | null) => {
    if (hoveredId == null) return baseWidth;
    return hoveredId === id ? baseWidth + 2 : baseWidth;
  };

  const hoverOpacity = (id: string | null) => {
    if (hoveredId == null) return 1;
    return hoveredId === id ? 1 : 0.3;
  };

  const legendConfigs: LegendConfig[] = [
    {
      id: 'current',
      display: t('yearCurrent', {year: String(current.year)}),
      makeIcon: () => makeLine({stroke: CURRENT_COLOR, strokeWidth: 2.5}),
    },
    ...highlighted.previousYears.map((yr, i) => ({
      id: `prev-${yr}`,
      display: String(yr),
      makeIcon: () => makeLine({stroke: PREV_YEAR_COLORS[i], strokeWidth: 1.8}),
    })),
    ...highlighted.highYears.map((yr, i) => ({
      id: `high-${yr}`,
      display: t('highYear', {year: String(yr)}),
      makeIcon: () =>
        makeLine({stroke: HIGH_YEAR_COLORS[i], strokeWidth: 1.4, strokeDasharray: '4 2'}),
    })),
    ...highlighted.lowYears.map((yr, i) => ({
      id: `low-${yr}`,
      display: t('lowYear', {year: String(yr)}),
      makeIcon: () =>
        makeLine({stroke: LOW_YEAR_COLORS[i], strokeWidth: 1.4, strokeDasharray: '4 2'}),
    })),
  ];

  return (
    <ChartFrame
      title={t('sweVolumeByElevationBandAnnual')}
      siteName={siteName}
      getCsvRows={() => annualLinesCsvRows(data, volumeUnit)}
    >
      <svg width={dims[0]} height={dims[1]}>
        <g transform={`translate(${BAND_MARGIN.left}, ${BAND_MARGIN.top})`}>
          <Axis
            axisCreator={d3.axisLeft(y).tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
            yLabelProps={{text: t('sweVolumeWithUnit', {unit: volumeUnit}), graphDims}}
          />
          <g transform={`translate(0, ${graphDims[1]})`}>
            <Axis axisCreator={d3.axisBottom(x).tickSizeOuter(0)} />
            <text
              x={graphDims[0] / 2}
              dy={36}
              textAnchor="middle"
              fontSize={12}
              className="fill-current text-gray-500"
            >
              {t('elevationBandWithUnit', {unit: elevationUnit})}
            </text>
          </g>

          {years.map(
            (yr) =>
              !highlightSet.has(yr.year) && (
                <path
                  key={yr.year}
                  d={makePath(yr.values)}
                  fill="none"
                  stroke={COLORS.AXIS_LIGHT}
                  strokeWidth={0.7}
                  opacity={hoverOpacity(null)}
                />
              )
          )}

          {years.map((yr) => {
            const style = getYearStyle(yr.year);
            if (!style) return null;
            return (
              <path
                key={yr.year}
                d={makePath(yr.values)}
                fill="none"
                stroke={style.stroke}
                strokeWidth={hoverWidth(style.strokeWidth, style.id)}
                strokeDasharray={style.dasharray}
                opacity={hoverOpacity(style.id)}
              />
            );
          })}

          <path
            d={makePath(current.values)}
            fill="none"
            stroke={CURRENT_COLOR}
            strokeWidth={hoverWidth(2.5, 'current')}
            opacity={hoverOpacity('current')}
          />
        </g>
      </svg>
      <HoverableLegend configs={legendConfigs} hoveredId={hoveredId} onHover={setHoveredId} />
    </ChartFrame>
  );
}

// ── Elevation Profile ───────────────────────────────────────────────────────

export function ElevationProfile({
  data,
  width,
  volumeUnit,
  elevationUnit,
  siteName,
  height = PROFILE_HEIGHT,
  showPercentOfAverage = true,
}: {
  data: SWEAnalysisResponse['elevationProfile'];
  width: number;
  volumeUnit: string;
  elevationUnit: string;
  siteName: string;
  height?: number;
  showPercentOfAverage?: boolean;
}) {
  const {t} = useTranslations();
  const {bandEdges, current: currentVol, climatology, summary} = data;
  const n = currentVol.length;
  const bandCenters = bandEdges.slice(0, n).map((e, i) => (e + bandEdges[i + 1]) / 2);
  const [hoveredBand, setHoveredBand] = React.useState<number | null>(null);

  const hasAnyData = currentVol.some((v) => (v ?? 0) > 0) || climatology.some((v) => (v ?? 0) > 0);
  if (!hasAnyData) {
    return (
      <div data-regional-report-elevation-empty className="text-sm text-gray-400 py-8">
        {t('noElevationDataAvailable')}
      </div>
    );
  }
  const range = bandEdges[n] - bandEdges[0];
  const pad = range * 0.1;
  const loElev = Math.max(0, bandEdges[0] - pad);
  const hiElev = bandEdges[n] + pad;

  const allVals = [...currentVol, ...climatology].filter((v): v is number => v != null && v > 0);
  const xMax = allVals.length > 0 ? Math.max(...allVals) * 1.1 : 1;

  const profileMargin = {...MARGIN, bottom: 42};
  const dims: Dims = [width, height];
  const graphDims = getGraphDims(dims, profileMargin);

  const yScale = d3.scaleLinear([loElev, hiElev], [graphDims[1], 0]);
  const xScale = d3.scaleLinear([0, xMax], [0, graphDims[0]]);
  const bandStep = bandEdges.length > 1 ? bandEdges[1] - bandEdges[0] : 100;
  const barH = (bandStep / (hiElev - loElev)) * graphDims[1];

  const legendConfigs: LegendConfig[] = [
    {
      id: 'current',
      display: t('current'),
      makeIcon: () => makeRect({fill: CURRENT_COLOR, fillOpacity: 0.85}),
    },
    {
      id: 'climatology',
      display: t('historicalAverage'),
      makeIcon: () => makeLine({stroke: CLIMATOLOGY_COLOR, strokeWidth: 1.5}),
    },
  ];

  return (
    <ChartFrame
      title={t(showPercentOfAverage ? 'sweByElevationPercentOfNormal' : 'sweByElevationVolume', {
        detail:
          `${defaultNumericTickFormat(summary.totalCurrent)} ${volumeUnit}` +
          (showPercentOfAverage && summary.percentOfHistoricalAvg != null
            ? `, ${t('percentOfHistoricalAvg', {pct: String(summary.percentOfHistoricalAvg)})}`
            : ''),
      })}
      siteName={siteName}
      getCsvRows={() => elevationProfileCsvRows(data, volumeUnit, elevationUnit)}
    >
      <svg width={dims[0]} height={dims[1]}>
        <g transform={`translate(${profileMargin.left}, ${profileMargin.top})`}>
          <Axis
            axisCreator={d3.axisLeft(yScale).tickFormat((d) => d.valueOf().toLocaleString())}
            yLabelProps={{text: t('elevationWithUnit', {unit: elevationUnit}), graphDims}}
          />
          <g transform={`translate(0, ${graphDims[1]})`}>
            <Axis
              axisCreator={d3
                .axisBottom(xScale)
                .tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
            />
            <text
              x={graphDims[0] / 2}
              dy={36}
              textAnchor="middle"
              fontSize={12}
              className="fill-current text-gray-500"
            >
              {t('sweVolumeWithUnit', {unit: volumeUnit})}
            </text>
          </g>

          {currentVol.map((v, i) => {
            if (v == null || v <= 0) return null;
            const elev = bandCenters[i];
            if (elev < loElev || elev > hiElev) return null;
            return (
              <rect
                key={i}
                x={0}
                y={yScale(elev) - barH / 2}
                width={xScale(v)}
                height={barH}
                fill={CURRENT_COLOR}
                fillOpacity={0.85}
                onMouseEnter={() => setHoveredBand(i)}
                onMouseLeave={() => setHoveredBand(null)}
              />
            );
          })}

          {hoveredBand != null && (currentVol[hoveredBand] ?? 0) > 0 && (
            <text
              x={xScale(currentVol[hoveredBand]!) + 4}
              y={yScale(bandCenters[hoveredBand]) + 4}
              fontSize={11}
              fontWeight="bold"
              fill="#333"
              stroke="white"
              strokeWidth={3}
              strokeLinejoin="round"
              style={{paintOrder: 'stroke fill'}}
            >
              {showPercentOfAverage && (climatology[hoveredBand] ?? 0) > 0
                ? t('percentOfAvg', {
                    pct: String(
                      Math.round((currentVol[hoveredBand]! / climatology[hoveredBand]!) * 100)
                    ),
                  })
                : `${defaultNumericTickFormat(currentVol[hoveredBand]!)} ${volumeUnit}`}
            </text>
          )}

          <path
            d={(() => {
              const pts: string[] = [];
              for (let i = 0; i < n; i++) {
                const v = climatology[i] ?? 0;
                const elev = bandEdges[i];
                if (elev < loElev || elev > hiElev) continue;
                const elevNext = bandEdges[i + 1];
                const px = xScale(v);
                pts.push(`${pts.length === 0 ? 'M' : 'L'}${px},${yScale(elev)}`);
                pts.push(`L${px},${yScale(elevNext)}`);
              }
              return pts.join(' ');
            })()}
            fill="none"
            stroke={CLIMATOLOGY_COLOR}
            strokeWidth={1.5}
          />
        </g>
      </svg>
      <StaticLegend configs={legendConfigs} />
    </ChartFrame>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function SWEAnalysis({
  siteId,
  projectId,
  siteName,
}: {
  siteId: string;
  projectId: string;
  siteName: string;
}) {
  const {t} = useTranslations();
  const unitSystem = useUnitSystem();
  const [data, setData] = React.useState<SWEAnalysisResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleRun = async () => {
    recordEvent('swe_analysis_clicked');
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await fetchSWEAnalysis(siteId, projectId, unitSystem);
      setData(result);
    } catch (e: any) {
      setError(e.message || t('failedToRunSweAnalysis'));
    } finally {
      setLoading(false);
    }
  };

  if (!data && !loading && !error) {
    return (
      <button
        onClick={handleRun}
        className="bg-blue-50 text-blue-600 py-1 px-2 rounded inline-flex items-center"
      >
        <SparkleIcon />
        {t('runSweAnalysis')}
      </button>
    );
  }

  if (loading) {
    return (
      <button
        disabled
        className="bg-blue-50 text-blue-400 py-1 px-2 rounded inline-flex items-center cursor-wait"
      >
        <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx={12}
            cy={12}
            r={10}
            stroke="currentColor"
            strokeWidth={4}
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {t('runningSweAnalysis')}
      </button>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={handleRun}
          className="bg-blue-50 text-blue-600 py-1 px-2 rounded inline-flex items-center"
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const [yearStart, yearEnd] = data.elevationBandBoxplot.yearRange;

  return (
    <WithWidth>
      {(width) => (
        <div className="space-y-8">
          <p className="text-sm text-gray-500">
            {t('sweDataSource', {yearStart: String(yearStart), yearEnd: String(yearEnd)})}
            {data.spatialCoverage < 100 &&
              ` ${t('spatialCoverage', {coverage: String(data.spatialCoverage)})}`}
          </p>
          <ElevationBandBoxplot
            data={data.elevationBandBoxplot}
            width={width}
            volumeUnit={data.volumeUnit}
            elevationUnit={data.elevationUnit}
            siteName={siteName}
          />
          <AnnualLines
            data={data.annualLines}
            width={width}
            volumeUnit={data.volumeUnit}
            elevationUnit={data.elevationUnit}
            siteName={siteName}
          />
          <ElevationProfile
            data={data.elevationProfile}
            width={width}
            volumeUnit={data.volumeUnit}
            elevationUnit={data.elevationUnit}
            siteName={siteName}
          />
        </div>
      )}
    </WithWidth>
  );
}
